import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import AdCard from '../components/AdCard';
import Seo from '../components/Seo';
import './CityHubPage.css';

// Deal-type sections linked from the hub. Kept in sync by hand with
// DealZy.Api/Helpers/SlugMaps.cs (DealSlugs).
const DEAL_SECTIONS = [
    { slug: 'kupit', label: 'Купить', dealType: '0' },
    { slug: 'snyat', label: 'Снять', dealType: '1' },
    { slug: 'posutochno', label: 'Снять посуточно', dealType: '2' },
    { slug: 'kommercheskaya-arenda', label: 'Коммерческая аренда', dealType: '3' },
    { slug: 'kommercheskaya-prodazha', label: 'Коммерческая продажа', dealType: '4' },
];

const PAGE_SIZE = 20;

// /nedvizhimost/{citySlug} - all deal types for one city, with links into the
// per-dealType catalog pages (/kupit/{citySlug}, /snyat/{citySlug}, etc).
const CityHubPage = () => {
    const { citySlug } = useParams();
    const [city, setCity] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [ads, setAds] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setCity(null);
        setNotFound(false);
        setLoading(true);
        api.get('/ads/cities').then(res => {
            if (cancelled) return;
            const match = (res.data ?? []).find(c => c.slug === citySlug);
            if (!match) { setNotFound(true); setLoading(false); return; }
            setCity(match);
        }).catch(() => { if (!cancelled) { setNotFound(true); setLoading(false); } });
        return () => { cancelled = true; };
    }, [citySlug]);

    useEffect(() => {
        if (!city) return;
        let cancelled = false;

        const listParams = new URLSearchParams();
        listParams.append('city', city.name);
        listParams.append('sort', 'newest');
        listParams.append('pageSize', String(PAGE_SIZE));

        Promise.all([
            api.get(`/ads?${listParams.toString()}`),
            ...DEAL_SECTIONS.map(s => api.get('/ads', { params: { city: city.name, dealType: s.dealType, pageSize: 1 } })),
        ]).then(([listRes, ...countRes]) => {
            if (cancelled) return;
            setAds(listRes.data.items ?? []);
            const nextCounts = {};
            DEAL_SECTIONS.forEach((s, i) => { nextCounts[s.slug] = countRes[i].data.totalCount ?? 0; });
            setCounts(nextCounts);
        }).catch(() => { if (!cancelled) { setAds([]); setCounts({}); } })
          .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [city]);

    if (notFound) {
        return (
            <div className="city-hub city-hub--empty">
                <p>Город не найден.</p>
                <Link to="/">На главную</Link>
            </div>
        );
    }

    const title = city ? `Недвижимость в ${city.prepositional || city.name}` : 'Недвижимость';
    const description = city
        ? `Объявления о недвижимости в ${city.prepositional || city.name} на Dealzy: покупка, аренда, посуточно, коммерческие помещения.`
        : undefined;

    return (
        <div className="city-hub">
            {city && <Seo title={title} description={description} />}

            <nav className="city-hub__breadcrumbs">
                <Link to="/">Главная</Link>
                {city && <><span>›</span><span>{title}</span></>}
            </nav>

            <h1 className="city-hub__title">{title}</h1>

            <div className="city-hub__sections">
                {DEAL_SECTIONS.map(s => (
                    <Link key={s.slug} className="city-hub__section" to={`/${s.slug}/${citySlug}`}>
                        {s.label}{counts[s.slug] ? ` (${counts[s.slug]})` : ''}
                    </Link>
                ))}
            </div>

            <div className="city-hub__grid">
                {loading ? (
                    <div className="city-hub__loading">Загрузка...</div>
                ) : ads.length === 0 ? (
                    <div className="city-hub__empty">Объявлений не найдено</div>
                ) : (
                    ads.map(ad => <AdCard key={ad.id} ad={ad} view="grid" listName={title} />)
                )}
            </div>
        </div>
    );
};

export default CityHubPage;
